import { reasonLabelForVolume } from '../sidebar/focusedReasonHelpers';
import { volumeStatusLabel } from '../sidebar/volumeStatusLabel';
import type { GroupingModel } from './types';
import {
  volumeAccountKey,
  volumePassedThroughInstance,
  withVolumeStatusOverlay,
} from './groupingPathShared';
import { clusterGrouping } from './clusterGrouping';

/**
 * No structural grouping is exposed in the UI — volumes sit directly under a
 * status bucket. Focusing a specific instance still needs its real
 * cluster/nodeGroup/instance nesting (so the tree can render just that
 * instance), so instance locations borrow the cluster model's shape.
 */
export const noneGrouping: GroupingModel = {
  secondary: 'none',

  locationForVolume(volume, opts) {
    const account = opts.groupByAccount ? volumeAccountKey(volume) : undefined;

    if (volume.instanceId && volumePassedThroughInstance(opts.currentLocation, volume)) {
      const instanceLocation = clusterGrouping.locationForInstance(volume, volume.instanceId, {
        ...opts,
        groupByAccount: false,
      });
      if (instanceLocation) {
        return withVolumeStatusOverlay({ ...instanceLocation, account }, volume, opts.view);
      }
    }

    return withVolumeStatusOverlay({ account, structural: { kind: 'empty' } }, volume, opts.view);
  },

  locationForInstance(anchor, instanceId, opts) {
    return clusterGrouping.locationForInstance(anchor, instanceId, opts);
  },

  scopeForLocation(volumes, location, opts) {
    if (location.structural.kind === 'cluster') {
      return clusterGrouping.scopeForLocation(volumes, location, opts);
    }

    let vols = volumes;
    if (location.account) vols = vols.filter((v) => volumeAccountKey(v) === location.account);
    if (location.statusLabel) {
      vols = vols.filter((v) => volumeStatusLabel(v, opts.view) === location.statusLabel);
    }
    if (location.focusedActivationKey) {
      vols = vols.filter((v) => v.activationStatus === location.focusedActivationKey);
    }
    if (location.reasonLabel) {
      vols = location.focusedActivationKey
        ? vols.filter(
            (v) => reasonLabelForVolume(v, location.focusedActivationKey!) === location.reasonLabel
          )
        : vols.filter((v) => v.supportLabel === location.reasonLabel);
    }
    return vols;
  },
};
